import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { formatApiError } from "../../lib/api";
import { Logo } from "../../components/Logo";

const ORG_TYPES = ["Business", "Church", "Real Estate", "Nonprofit", "Education"];

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", organization_name: "", organization_type: "Business" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(form);
      toast.success("Workspace created — welcome!");
      navigate("/app/dashboard");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Registration failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-alto-black text-white">
      <div className="hidden lg:flex flex-col justify-between p-12 marketing-grid-bg border-r border-white/10">
        <Link to="/"><Logo dark size={34} /></Link>
        <div>
          <h1 className="font-display text-4xl font-extrabold leading-tight">Start capturing<br />every contact.</h1>
          <p className="text-neutral-400 mt-5 max-w-sm text-sm">Create your workspace and set up your organization in seconds. We'll configure sources and a starting pipeline for you.</p>
        </div>
        <p className="text-xs text-neutral-600">© {new Date().getFullYear()} AllToMAIDIt Solutions</p>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12 overflow-y-auto">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-8"><Link to="/"><Logo dark size={32} /></Link></div>
          <h2 className="font-display text-3xl font-bold">Start your trial</h2>
          <p className="text-neutral-400 text-sm mt-2">Free to start. No credit card required.</p>
          <form onSubmit={submit} className="space-y-4 mt-8" data-testid="register-form">
            <In label="Your name" value={form.name} onChange={set("name")} testid="register-name" required />
            <In label="Email" type="email" value={form.email} onChange={set("email")} testid="register-email" required />
            <In label="Password (min 8 chars)" type="password" value={form.password} onChange={set("password")} testid="register-password" required />
            <In label="Organization name" value={form.organization_name} onChange={set("organization_name")} testid="register-org" required />
            <div>
              <label className="block text-xs font-semibold text-neutral-400 mb-1.5">Organization type</label>
              <select value={form.organization_type} onChange={set("organization_type")} data-testid="register-org-type" className="w-full bg-alto-charcoal border border-white/15 rounded-md px-4 py-3 text-sm focus:border-alto-red outline-none">
                {ORG_TYPES.map((t) => <option key={t}>{t}</option>)}
              </select>
            </div>
            <button type="submit" disabled={loading} data-testid="register-submit" className="btn-red w-full font-bold px-6 py-3.5 rounded-md inline-flex items-center justify-center gap-2 text-sm disabled:opacity-60">
              {loading ? "Creating…" : <>START YOUR TRIAL <ArrowRight size={16} /></>}
            </button>
          </form>
          <p className="text-sm text-neutral-400 mt-6">Already have an account? <Link to="/app/login" data-testid="to-login" className="text-alto-red font-semibold">Sign in</Link></p>
          <Link to="/" className="text-xs text-neutral-600 mt-8 inline-block hover:text-neutral-400">← Back to website</Link>
        </div>
      </div>
    </div>
  );
}

function In({ label, value, onChange, type = "text", testid, required }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-neutral-400 mb-1.5">{label}</label>
      <input type={type} value={value} onChange={onChange} data-testid={testid} required={required} className="w-full bg-alto-charcoal border border-white/15 rounded-md px-4 py-3 text-sm focus:border-alto-red outline-none" />
    </div>
  );
}
